import { useTranslation } from 'react-i18next';
import { Filter, Building2, CalendarRange } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import type { PeriodKey } from '@/lib/report-metrics';

type Props = {
  period: PeriodKey;
  onPeriodChange: (p: PeriodKey) => void;
  customFrom: string;
  customTo: string;
  onCustomFromChange: (v: string) => void;
  onCustomToChange: (v: string) => void;
  branchId: string;
  onBranchChange: (id: string) => void;
  branches: { id: string; name: string }[];
};

export function ReportFilters({
  period, onPeriodChange, customFrom, customTo,
  onCustomFromChange, onCustomToChange, branchId, onBranchChange, branches,
}: Props) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={branchId} onValueChange={onBranchChange}>
        <SelectTrigger className="w-[180px]">
          <Building2 size={14} className="text-muted-foreground mr-1" />
          <SelectValue placeholder={t('tick_reports.allBranches')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">{t('tick_reports.allBranches')}</SelectItem>
          {branches.map((b) => (
            <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={period} onValueChange={(v) => onPeriodChange(v as PeriodKey)}>
        <SelectTrigger className="w-[150px]">
          <Filter size={14} className="text-muted-foreground mr-1" />
          <SelectValue placeholder={t('tick_reports.allPeriodShort')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="today">{t('tick_reports.today')}</SelectItem>
          <SelectItem value="7days">{t('tick_reports.days7')}</SelectItem>
          <SelectItem value="week">{t('tick_reports.thisWeek')}</SelectItem>
          <SelectItem value="30days">{t('tick_reports.days30')}</SelectItem>
          <SelectItem value="month">{t('tick_reports.thisMonth')}</SelectItem>
          <SelectItem value="all">{t('tick_reports.allPeriodShort')}</SelectItem>
          <SelectItem value="custom">{t('tick_reports.customRange')}</SelectItem>
        </SelectContent>
      </Select>

      {period === 'custom' && (
        <div className="flex items-center gap-2">
          <CalendarRange size={14} className="text-muted-foreground" />
          <Input
            type="date"
            value={customFrom}
            onChange={(e) => onCustomFromChange(e.target.value)}
            className="w-[145px]"
            aria-label={t('tick_reports.dateFrom')}
          />
          <span className="text-muted-foreground text-sm">—</span>
          <Input
            type="date"
            value={customTo}
            onChange={(e) => onCustomToChange(e.target.value)}
            className="w-[145px]"
            aria-label={t('tick_reports.dateTo')}
          />
        </div>
      )}
    </div>
  );
}
